// Pure helpers for container-level customs payment allocation (Bill of Entry / Tax Bill / Port
// Charges). No Supabase/React imports — kept unit-testable in isolation.

/**
 * Splits `total` into `n` shares that sum EXACTLY to `total`, doing the division in integer cents
 * so float division never leaves the sum off by a fraction of a cent (`financial-math-rules`:
 * round only at display, never percentage-split money). Every share gets the floor amount; the
 * leftover cents from that division land entirely on the LAST share (never distributed one cent at
 * a time across the front, so the allocation is deterministic regardless of member order).
 *
 * `n <= 0` returns `[]`. `total` may be negative; the same floor/remainder-to-last logic still sums
 * exactly.
 */
export function splitEvenWithRemainderToLast(total: number, n: number): number[] {
    if (n <= 0) return [];

    const totalCents = Math.round(total * 100);
    const baseCents = Math.trunc(totalCents / n);
    const remainderCents = totalCents - baseCents * n;

    const shares = new Array<number>(n).fill(baseCents / 100);
    shares[n - 1] = (baseCents + remainderCents) / 100;
    return shares;
}

/** DB column on `bills_of_entry` that links each payment type's assigned transaction — shared
 * between CustomsPaymentsPanel (per-container split) and VehicleTransactionAssigner (single-vehicle
 * assign/unassign) so both write/clear the same column name for a given type. */
export const BOE_LINK_COLUMNS: Record<'tax_bill' | 'port_charges' | 'levy', 'tax_transaction_id' | 'port_charges_transaction_id' | 'levy_transaction_id'> = {
    tax_bill: 'tax_transaction_id',
    port_charges: 'port_charges_transaction_id',
    levy: 'levy_transaction_id',
};

/** A container/BOE member eligible to receive a share of a payment. */
export interface AllocationMember {
    vehicleId: number;
    vin: string;
    label: string;
    existingTransactionId: number | null;
}

/** One row of the allocation table: a member's proposed share, editable unless already assigned. */
export interface AllocationRow {
    vehicleId: number;
    vin: string;
    label: string;
    amount: number;
    fee: number;
    editable: boolean;
    /** The BOE-assessed figure this row was prefilled from, when `amount` came from BOE data (see
     * `buildAllocationRows`) — undefined when the even-split fallback was used instead. Since the
     * owner ruling of 2026-07-21, `amount` is always prefilled verbatim from this figure (never
     * reconciled), so at prefill time the two are equal; they diverge only through manual edits. */
    boeAmount?: number;
}

/**
 * Builds the allocation table for a payment being split across `eligibleMembers`.
 *
 * Amount prefill: when EVERY eligible member has a BOE-parsed amount in `boeAmountsByVehicleId`,
 * ALL rows use those exact per-vehicle figures VERBATIM (owner ruling 2026-07-21) — they are never
 * adjusted to reconcile with the paid transaction amount; the document-level remainder stays on the
 * container transaction and the UI surfaces the difference as information. If even one eligible
 * member is missing a BOE figure, fall back to an even split of `total` across all eligible members
 * (never a partial mix of BOE + even-split, which would silently misrepresent the assessed
 * amounts).
 *
 * Fee is always split evenly across eligible members, independent of how the amount was sourced —
 * the bank fee has no per-vehicle assessed figure to prefill from.
 *
 * A member already holding this transaction type (`existingTransactionId != null`) is not
 * "eligible" for a NEW split — the caller filters those out of `eligibleMembers` before calling this
 * (per the plan: shown grayed "Already assigned" and excluded). `editable` is true for every row
 * here since every row is, by construction, an eligible (not-yet-assigned) member.
 */
export function buildAllocationRows(
    eligibleMembers: AllocationMember[],
    total: number,
    fee: number,
    boeAmountsByVehicleId: Map<number, number> | null,
): AllocationRow[] {
    const n = eligibleMembers.length;
    if (n === 0) return [];

    const fees = splitEvenWithRemainderToLast(fee, n);

    const everyMemberHasBoeAmount = boeAmountsByVehicleId !== null
        && eligibleMembers.every(m => boeAmountsByVehicleId.has(m.vehicleId));

    if (everyMemberHasBoeAmount) {
        // BOE figures are used VERBATIM — never adjusted to reconcile with the paid transaction
        // amount (owner ruling 2026-07-21). The document-level remainder (fees/levies GRA counts
        // only in the grand total) stays on the container transaction; the UI surfaces the
        // difference as information instead of forcing the rows to sum to the payment.
        const boeAmounts = eligibleMembers.map(m => boeAmountsByVehicleId!.get(m.vehicleId)!);

        return eligibleMembers.map((m, idx) => ({
            vehicleId: m.vehicleId,
            vin: m.vin,
            label: m.label,
            amount: boeAmounts[idx],
            fee: fees[idx],
            editable: true,
            boeAmount: boeAmounts[idx],
        }));
    }

    const amounts = splitEvenWithRemainderToLast(total, n);
    return eligibleMembers.map((m, idx) => ({
        vehicleId: m.vehicleId,
        vin: m.vin,
        label: m.label,
        amount: amounts[idx],
        fee: fees[idx],
        editable: true,
    }));
}

/** Result of comparing a parsed Bill of Entry's VINs against a container's actual membership. */
export interface VinDiffResult {
    matchedVins: string[];
    parsedOnlyVins: string[];
    unmatchedMemberVins: string[];
}

// Strip punctuation, not just whitespace: the fleet stores chassis numbers dashed
// ("NSP130-2115255") while GRA Bill of Entry documents print them undashed
// ("NSP1302115255") — the two must compare equal.
const normalizeVin = (vin: string): string => vin.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Diffs the VINs parsed off a Bill of Entry against a container's actual member VINs.
 * Case/whitespace-insensitive (documents are OCR/typed inconsistently); comparison and de-duping
 * both use the normalized form, but the returned lists preserve the ORIGINAL casing/whitespace of
 * whichever side's array of that entry — parsed lists return parsed-side originals,
 * `unmatchedMemberVins` returns member-side originals.
 */
export function diffParsedVinsAgainstMembership(parsedVins: string[], memberVins: string[]): VinDiffResult {
    const normalizedMemberVins = new Set(memberVins.map(normalizeVin));
    const normalizedParsedVins = new Set(parsedVins.map(normalizeVin));

    const seenMatched = new Set<string>();
    const seenParsedOnly = new Set<string>();
    const matchedVins: string[] = [];
    const parsedOnlyVins: string[] = [];

    for (const vin of parsedVins) {
        const normalized = normalizeVin(vin);
        if (normalizedMemberVins.has(normalized)) {
            if (!seenMatched.has(normalized)) {
                seenMatched.add(normalized);
                matchedVins.push(vin);
            }
        } else if (!seenParsedOnly.has(normalized)) {
            seenParsedOnly.add(normalized);
            parsedOnlyVins.push(vin);
        }
    }

    const seenUnmatchedMember = new Set<string>();
    const unmatchedMemberVins: string[] = [];
    for (const vin of memberVins) {
        const normalized = normalizeVin(vin);
        if (!normalizedParsedVins.has(normalized) && !seenUnmatchedMember.has(normalized)) {
            seenUnmatchedMember.add(normalized);
            unmatchedMemberVins.push(vin);
        }
    }

    return { matchedVins, parsedOnlyVins, unmatchedMemberVins };
}
